import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Radio, RefreshCw, Search } from 'lucide-react';
import GrowthTabNav from './GrowthTabNav';
import GrowthDateFilter from './GrowthDateFilter';
import { getGrowthEvents } from '../../Services/GrowthService';
import { GrowthEvent } from '../../Types/GrowthType';
import { PaginationResponse } from '../../../Shared/Pagination/PaginationResponse';
import Pagination from '../../../Shared/Pagination/Pagination';
import { formatDateTime } from '../../../Shared/Utils/HelperUtils';

const PAGE_SIZE = 15;

const headerClass =
  'px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500';

function GrowthEventsComponent() {
  const [pageData, setPageData] = useState<PaginationResponse<GrowthEvent> | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');
  const [eventName, setEventName] = useState('');
  const [appliedEventName, setAppliedEventName] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getGrowthEvents({
        fromDate: appliedFrom || undefined,
        toDate: appliedTo || undefined,
        eventName: appliedEventName || undefined,
        page,
        size: PAGE_SIZE,
      });
      if (response.success && response.data) {
        setPageData(response.data);
      }
    } catch (err) {
      console.error('Failed to load growth events', err);
    } finally {
      setLoading(false);
    }
  }, [page, appliedFrom, appliedTo, appliedEventName]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(0);
  }, [appliedFrom, appliedTo, appliedEventName]);

  const content = pageData?.content || [];
  const totalElements = pageData?.totalElements || 0;
  const totalPages = pageData?.totalPages || 0;

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <GrowthTabNav />

      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
          <Radio size={22} />
        </span>
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Acquisition Events</h1>
          <p className="text-xs font-semibold text-slate-400 md:text-sm">
            Raw tracked visitor &amp; acquisition events
          </p>
        </div>
        <button
          onClick={fetchData}
          title="Refresh"
          className="ml-auto inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-3 xl:grid-cols-2">
        <GrowthDateFilter
          fromDate={fromDate}
          toDate={toDate}
          onApply={(f, t) => {
            setFromDate(f);
            setToDate(t);
            setAppliedFrom(f);
            setAppliedTo(t);
          }}
          extra={
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setAppliedEventName(eventName);
                }}
                onBlur={() => setAppliedEventName(eventName)}
                placeholder="Filter by event name..."
                className="rounded-xl border border-slate-800 bg-slate-950/60 py-2 pl-9 pr-3 text-xs font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50"
              />
            </div>
          }
        />
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40">
        <table className="w-full min-w-[1100px] text-left">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-900/60">
              {['Event', 'Visitor', 'Page URL', 'UTM Source', 'Campaign', 'Device / Country', 'User', 'Created'].map((h) => (
                <th key={h} className={headerClass}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/70">
            {content.map((event, idx) => (
              <motion.tr
                key={event.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.02 }}
                className="transition hover:bg-slate-900/70"
              >
                <td className="px-5 py-4">
                  <span className="inline-flex rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-1 text-[11px] font-bold text-cyan-400">
                    {event.eventName}
                  </span>
                </td>
                <td className="px-5 py-4 font-mono text-xs font-semibold text-slate-400">
                  {event.visitorId ? `${event.visitorId.slice(0, 12)}...` : '—'}
                </td>
                <td className="max-w-[240px] truncate px-5 py-4 text-xs font-semibold text-slate-300">{event.pageUrl || '—'}</td>
                <td className="px-5 py-4 text-xs font-semibold text-slate-400">
                  {event.source || '—'}
                  {event.medium && <span className="text-slate-600"> / {event.medium}</span>}
                </td>
                <td className="px-5 py-4 text-xs font-semibold text-slate-400">{event.campaign || '—'}</td>
                <td className="px-5 py-4 text-xs font-semibold text-slate-400">
                  {[event.deviceCategory, event.country].filter(Boolean).join(' · ') || '—'}
                </td>
                <td className="px-5 py-4 font-mono text-xs font-semibold text-slate-500">
                  {event.userId ? `${event.userId.slice(0, 8)}...` : '—'}
                </td>
                <td className="px-5 py-4 text-xs font-semibold text-slate-500">{formatDateTime(event.createdAt)}</td>
              </motion.tr>
            ))}
            {content.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="px-5 py-16 text-center text-sm font-semibold text-slate-500">
                  No events match the current filters.
                </td>
              </tr>
            )}
            {loading && content.length === 0 && (
              <tr>
                <td colSpan={8} className="px-5 py-16 text-center text-sm font-semibold text-slate-500">
                  <RefreshCw className="mx-auto h-6 w-6 animate-spin text-cyan-500" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalElements > 0 && (
        <div className="mt-2 rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-2">
          <Pagination
            page={page}
            size={PAGE_SIZE}
            totalElements={totalElements}
            totalPages={totalPages}
            first={pageData?.first ?? page === 0}
            last={pageData?.last ?? false}
            hasNext={pageData?.hasNext ?? false}
            hasPrevious={pageData?.hasPrevious ?? page > 0}
            setPage={setPage}
          />
        </div>
      )}
    </div>
  );
}

export default GrowthEventsComponent;